import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertCircle, ExternalLink, Loader2 } from "lucide-react";

const StripeConnectTab = () => {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stripe/connect/status`);
      setStripeStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Stripe status:", error);
      // If no account, status will be false
      setStripeStatus({
        connected: false,
        onboarding_complete: false,
        charges_enabled: false,
        payouts_enabled: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setOnboarding(true);
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const response = await axios.post(`${API}/stripe/connect/onboarding`, {
        refresh_url: currentUrl + '?stripe_refresh=true',
        return_url: currentUrl + '?stripe_return=true'
      });
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Failed to start Stripe onboarding:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Stripe account");
      setOnboarding(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await axios.post(`${API}/stripe/connect/dashboard-link`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error("Failed to open Stripe dashboard:", error);
      toast.error("Failed to open Stripe dashboard");
    }
  };

  // Check if we're returning from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_return') === 'true') {
      toast.success("Stripe account connected! Checking status...");
      // Refresh status after successful onboarding
      setTimeout(() => {
        fetchStripeStatus();
      }, 2000);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (urlParams.get('stripe_refresh') === 'true') {
      toast.info("Please complete the Stripe onboarding process");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <CreditCard className="w-6 h-6" />
            Stripe Payment Account
          </CardTitle>
          <CardDescription>
            Connect your club's Stripe account to receive payments when companies purchase your volunteer hours
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Connection Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-sm text-gray-600 mb-2">Connection Status</p>
              <div className="flex items-center gap-2">
                {stripeStatus.connected ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="font-semibold text-green-700">Connected</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-600">Not Connected</span>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-sm text-gray-600 mb-2">Onboarding Status</p>
              <div className="flex items-center gap-2">
                {stripeStatus.onboarding_complete ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="font-semibold text-green-700">Complete</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold text-amber-700">
                      {stripeStatus.connected ? "Incomplete" : "Not Started"}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-sm text-gray-600 mb-2">Payments Enabled</p>
              <div className="flex items-center gap-2">
                {stripeStatus.charges_enabled ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="font-semibold text-green-700">Yes</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="font-semibold text-red-700">No</span>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <p className="text-sm text-gray-600 mb-2">Payouts Enabled</p>
              <div className="flex items-center gap-2">
                {stripeStatus.payouts_enabled ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="font-semibold text-green-700">Yes</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="font-semibold text-red-700">No</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {!stripeStatus.connected ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 mb-3">
                  <strong>Important:</strong> Your club must connect a Stripe account before your volunteer hours can be sold to companies. 
                  This ensures you receive your 90% share of all sales automatically.
                </p>
                <Button
                  onClick={handleConnectStripe}
                  disabled={onboarding}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {onboarding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Connect Stripe Account
                    </>
                  )}
                </Button>
              </div>
            ) : !stripeStatus.onboarding_complete ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-3">
                  <strong>Action Required:</strong> Your Stripe account is connected but onboarding is incomplete. 
                  Please complete the onboarding process to start receiving payments.
                </p>
                <Button
                  onClick={handleConnectStripe}
                  disabled={onboarding}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {onboarding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      Complete Stripe Onboarding
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-800 mb-1">
                      ✓ All Set! Your club is ready to receive payments
                    </p>
                    <p className="text-sm text-green-700 mb-3">
                      When companies purchase volunteer hours from your club, 90% of the payment will automatically be transferred to your Stripe account.
                    </p>
                    {stripeStatus.email && (
                      <p className="text-xs text-green-600 mb-3">
                        Connected account: {stripeStatus.email}
                      </p>
                    )}
                    <Button
                      onClick={handleOpenDashboard}
                      variant="outline"
                      className="border-green-300 text-green-700 hover:bg-green-50"
                      size="sm"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">How It Works</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">1.</span>
                <span>Connect your club's Stripe account using the button above</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">2.</span>
                <span>Complete the Stripe onboarding process (identity verification, bank details)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">3.</span>
                <span>When companies purchase your volunteer hours, 90% is automatically sent to your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">4.</span>
                <span>View all transactions and payouts in your Stripe Dashboard</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeConnectTab;
